import React from 'react';
import {
  Box,
  TextField,
  Typography,
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  Skeleton,
} from '@mui/material';
import type { PartyState } from './types';
import './EditFileModal.scss';
import { useTranslation } from 'react-i18next';

// ─── Loading Skeleton ───────────────────────────────────────────────────────

export const EditFormSkeleton: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Box>
      <Box className="edit-file-skeleton__group edit-file-skeleton__group--party">
        <Typography
          variant="subtitle2"
          className="edit-file-skeleton__label"
          color="text.secondary"
        >
          {t('Party Information')}
        </Typography>
        <Box className="edit-file-skeleton__group">
          <Skeleton variant="rectangular" height={40} />
          <Skeleton variant="rectangular" height={40} />
        </Box>
      </Box>
      <Box>
        <Typography
          variant="subtitle2"
          className="edit-file-skeleton__label"
          color="text.secondary"
        >
          {t('Contract Details')}
        </Typography>
        <Box className="edit-file-skeleton__group">
          <Skeleton variant="rectangular" height={40} />
          <Skeleton variant="rectangular" height={40} />
          <Skeleton variant="rectangular" height={40} />
        </Box>
      </Box>
    </Box>
  );
};

// ─── Party Information Section ──────────────────────────────────────────────

type PartyInformationSectionProps = {
  parties: PartyState[];
  onPartyNameChange: (index: number, value: string) => void;
};

export const PartyInformationSection: React.FC<
  PartyInformationSectionProps
> = ({ parties, onPartyNameChange }) => (
  <Box className="party-grid">
    {parties.map((party, index) => (
      <TextField
        key={index}
        label={`Party ${index + 1}`}
        value={party.name}
        onChange={(e) => onPartyNameChange(index, e.target.value)}
        fullWidth
        variant="outlined"
        size="small"
        required={index < 2}
        className="edit-file-input"
      />
    ))}
  </Box>
);

// ─── Signature Section ──────────────────────────────────────────────────────

type SignatureSectionProps = {
  documentSigned: boolean;
  onDocumentSignedChange: (checked: boolean) => void;
  parties: PartyState[];
  onTogglePartySigned: (index: number) => void;
  signingDate: string;
  onSigningDateChange: (value: string) => void;
  signatureError?: string | null;
};

export const SignatureSection: React.FC<SignatureSectionProps> = ({
  documentSigned,
  onDocumentSignedChange,
  parties,
  onTogglePartySigned,
  signingDate,
  onSigningDateChange,
  signatureError,
}) => {
  const { t } = useTranslation();
  return (
    <Box className="signature-section">
      <FormControlLabel
        control={
          <Checkbox
            checked={documentSigned}
            onChange={(e) => onDocumentSignedChange(e.target.checked)}
            className="signature-section__checkbox"
          />
        }
        label={
          <Typography className="signature-section__label">
            {t('Mark this document as signed')}
          </Typography>
        }
      />

      <Collapse in={documentSigned}>
        <Box className="signature-collapse-content">
          {/* Party Chips */}
          <Box className="signature-chips">
            <Typography
              variant="caption"
              className="signature-chips__label"
              color="text.secondary"
            >
              {t('Signed by:')}
            </Typography>
            <Box className="signature-chips__list">
              {parties.map((party, index) => (
                <Chip
                  key={index}
                  label={party.name || `Party ${index + 1}`}
                  onClick={() => onTogglePartySigned(index)}
                  variant={party.signed ? 'filled' : 'outlined'}
                  className={`signature-chip ${
                    party.signed
                      ? 'signature-chip--signed'
                      : 'signature-chip--unsigned'
                  }`}
                />
              ))}
            </Box>
            {signatureError && (
              <Typography variant="caption" className="signature-chips__error">
                {signatureError}
              </Typography>
            )}
          </Box>

          {/* Signing Date */}
          <TextField
            label={t('Signed on date')}
            type="date"
            value={signingDate}
            onChange={(e) => onSigningDateChange(e.target.value)}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
            className="signature-date-input"
          />
        </Box>
      </Collapse>
    </Box>
  );
};

// ─── Contract Details Section ───────────────────────────────────────────────

type ContractDetailsSectionProps = {
  title: string;
  onTitleChange: (value: string) => void;
  filename: string;
  onFilenameChange: (value: string) => void;
  contractDate: string;
  onContractDateChange: (value: string) => void;
};

export const ContractDetailsSection: React.FC<ContractDetailsSectionProps> = ({
  title,
  onTitleChange,
  filename,
  onFilenameChange,
  contractDate,
  onContractDateChange,
}) => {
  const { t } = useTranslation();
  return (
    <Box className="edit-file-section">
      <Typography variant="subtitle2" className="edit-file-section__title">
        {t('Contract Details')}
      </Typography>
      <Box className="contract-details__fields">
        <TextField
          label={t('Title')}
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          fullWidth
          variant="outlined"
          size="small"
          className="edit-file-input"
        />
        <TextField
          label={t('Filename')}
          value={filename}
          onChange={(e) => onFilenameChange(e.target.value)}
          fullWidth
          variant="outlined"
          size="small"
          className="edit-file-input"
        />
        <TextField
          label={t('Contract Date')}
          type="date"
          value={contractDate}
          onChange={(e) => onContractDateChange(e.target.value)}
          fullWidth
          variant="outlined"
          size="small"
          InputLabelProps={{ shrink: true }}
          className="edit-file-input"
        />
      </Box>
    </Box>
  );
};

// ─── Wrapper: Party + Signature combined section ────────────────────────────

type PartySectionWrapperProps = PartyInformationSectionProps &
  SignatureSectionProps;

export const PartySectionWrapper: React.FC<PartySectionWrapperProps> = ({
  parties,
  onPartyNameChange,
  documentSigned,
  onDocumentSignedChange,
  onTogglePartySigned,
  signingDate,
  onSigningDateChange,
  signatureError,
}) => {
  const { t } = useTranslation();
  return (
    <Box className="edit-file-section edit-file-section--party">
      <Typography variant="subtitle2" className="edit-file-section__title">
        {t('Party Information')}
      </Typography>

      <PartyInformationSection
        parties={parties}
        onPartyNameChange={onPartyNameChange}
      />

      <SignatureSection
        documentSigned={documentSigned}
        onDocumentSignedChange={onDocumentSignedChange}
        parties={parties}
        onTogglePartySigned={onTogglePartySigned}
        signingDate={signingDate}
        onSigningDateChange={onSigningDateChange}
        signatureError={signatureError}
      />
    </Box>
  );
};
